// src/pages/HomePage.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import ProductCard from "../components/ProductCard";
import {
  getUserPreferredCityKey,
  sortProductsByNearestCity,
} from "../utils/nigerLocations";
import { localizedCategoryName } from "../utils/i18nContent";
import { useAppData } from "../context/AppDataContext";
import { formatPrice } from "../utils/formatPrice";
import { formatStockDisplay } from "../utils/stockFormatter";
import Loader from "../components/Loader";
import {
  FiArrowRight,
  FiChevronRight,
  FiChevronLeft,
  FiTrendingUp,
  FiStar,
  FiMapPin,
  FiBox,
  FiShield,
} from "react-icons/fi";
import { IoSparkles } from "react-icons/io5";

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { currency, rates } = useAppData();
  const [bestSellers, setBestSellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [userCityKey, setUserCityKey] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const syncUserCity = () => setUserCityKey(getUserPreferredCityKey());
    syncUserCity();
    window.addEventListener("chafadi-addresses-updated", syncUserCity);
    return () => window.removeEventListener("chafadi-addresses-updated", syncUserCity);
  }, []);

  useEffect(() => {
    const fetchHomepageData = async () => {
      setLoading(true);
      try {
        const [hotRes, arrivalRes, featuredRes, categoriesRes] = await Promise.all([
          api.get("/products/hot-selling/"),
          api.get("/products/new-arrivals/"),
          api.get("/products/?is_featured=true"),
          api.get("/categories/"),
        ]);

        setBestSellers(hotRes.data.results || hotRes.data);
        setNewArrivals(arrivalRes.data.results || arrivalRes.data);
        setFeaturedProducts(featuredRes.data.results || featuredRes.data);

        let cats = categoriesRes.data.results || categoriesRes.data;
        cats = cats
          .filter((c) => !c.parent_id && (c.product_count > 0 || c.product_count === undefined))
          .sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
        setCategories(cats);

        const catProductsArr = await Promise.all(
          cats.map((cat) =>
            api
              .get(`/products/?category=${cat.id}&page_size=10`)
              .then((r) => ({ id: cat.id, products: r.data.results || r.data }))
              .catch(() => ({ id: cat.id, products: [] }))
          )
        );

        const catMap = {};
        catProductsArr.forEach(({ id, products }) => {
          catMap[id] = products;
        });
        setCategoryProducts(catMap);
      } catch (err) {
        console.error("Failed to load homepage data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageData();
  }, []);

  useEffect(() => {
    if (featuredProducts && featuredProducts.length > 1 && !isPaused) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
      }, 6500);
      return () => clearInterval(interval);
    }
  }, [featuredProducts, isPaused]);

  const rankedBestSellers = useMemo(
    () => sortProductsByNearestCity(bestSellers, userCityKey),
    [bestSellers, userCityKey]
  );

  const rankedNewArrivals = useMemo(
    () => sortProductsByNearestCity(newArrivals, userCityKey),
    [newArrivals, userCityKey]
  );

  const handleNext = () => {
    if (featuredProducts.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
    }
  };

  const handlePrev = () => {
    if (featuredProducts.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + featuredProducts.length) % featuredProducts.length);
    }
  };

  if (loading) {
    return <Loader />;
  }

  // Active product calculation
  const activeProduct =
    featuredProducts && featuredProducts.length > 0
      ? featuredProducts[currentSlide % featuredProducts.length]
      : null;

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-primary)]">
      {/* ── 1. FEATURED SHOWCASE ── */}
      {featuredProducts && featuredProducts.length > 0 && (
        <section className="relative px-2 sm:px-6 lg:px-8 pb-4 pt-1">
          <div className="mx-auto max-w-7xl">

            {/* ══════════════════════════════════════════════════════════════
                A. DESKTOP SHOWCASE (Tailored for Large Screens >= 1024px)
               ══════════════════════════════════════════════════════════════ */}
            <div
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              className="hidden lg:block relative w-full h-[470px] overflow-hidden rounded-[32px] bg-[#0c0c0f] border border-amber-500/25 shadow-[0_20px_60px_rgba(0,0,0,0.3)] group"
            >
              {/* Golden Top Shimmer Edge */}
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#ffd700] to-transparent z-30 opacity-80 pointer-events-none" />

              {/* Ambient Golden Radial Flares */}
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-[#ffd700]/15 via-[#ff9f0a]/10 to-transparent blur-3xl pointer-events-none z-10" />
              <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-gradient-to-tr from-[#ff5e00]/15 via-[#ffd700]/10 to-transparent blur-3xl pointer-events-none z-10" />

              {featuredProducts.map((p, idx) => {
                const isCurrent = idx === currentSlide;
                const priceValue = parseFloat(p.price) || 0;
                const formattedPrice = formatPrice(
                  { min: priceValue, max: priceValue },
                  currency,
                  rates
                );
                const originalPrice = p.discount_percentage > 0
                  ? formatPrice(
                      { min: priceValue / (1 - p.discount_percentage / 100), max: priceValue / (1 - p.discount_percentage / 100) },
                      currency,
                      rates
                    )
                  : null;
                const stockInfo = formatStockDisplay(p.stock !== undefined ? p.stock : p.quantity);
                const imageUrl =
                  p.featured_image_url ||
                  p.thumbnail_url ||
                  (p.images && p.images[0]?.image) ||
                  "/placeholder-image.jpg";

                return (
                  <div
                    key={p.id}
                    className={`absolute inset-0 transition-opacity duration-700 ease-[cubic-bezier(0.28,0.11,0.32,1)] ${
                      isCurrent
                        ? "opacity-100 z-10"
                        : "opacity-0 pointer-events-none z-0"
                    }`}
                  >
                    {/* Full Landscape Visual */}
                    <img
                      src={imageUrl}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-1000 ease-out group-hover:scale-105"
                    />

                    {/* Cinematic Gradient Scrim: Deep contrast on the left, clear view on the right */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/30 w-full pointer-events-none" />

                    {/* ── Left Editorial Column (65% width) ── */}
                    <div className="relative z-20 flex h-full flex-col justify-center p-12 max-w-[68%] space-y-4">
                      {/* Badge Ribbon */}
                      <div className="flex flex-wrap items-center gap-2.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#ffe259] via-[#ffa751] to-[#ff6a00] px-3.5 py-1 text-[11px] font-black text-black tracking-wider uppercase shadow-[0_4px_16px_rgba(255,180,0,0.45)] border border-yellow-200/60">
                          <IoSparkles size={13} className="text-black" />
                          <span>{t("homePage.featureAsset", "Exclusive Featured")}</span>
                        </span>

                        {p.category && (
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/90 px-3 py-1 bg-black/40 backdrop-blur-md border border-white/15 rounded-full">
                            {localizedCategoryName(p.category, i18n.language)}
                          </span>
                        )}

                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-300 px-3 py-1 bg-amber-500/15 backdrop-blur-md border border-amber-400/30 rounded-full">
                          <FiShield size={13} className="text-amber-400" />
                          <span>Verified Supplier</span>
                        </span>

                        {p.discount_percentage > 0 && (
                          <span className="inline-flex items-center rounded-full bg-red-500/90 backdrop-blur-md px-2.5 py-1 text-[11px] font-extrabold text-white shadow-sm">
                            -{p.discount_percentage}% OFF
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <Link to={`/products/${p.id}`} className="no-underline group/link block">
                        <h1 className="text-3xl xl:text-4xl font-black text-white tracking-tight leading-[1.15] line-clamp-2 drop-shadow-lg group-hover/link:text-amber-300 transition-colors">
                          {p.name}
                        </h1>
                      </Link>

                      {/* Specs / Meta Strip */}
                      <div className="flex flex-wrap items-center gap-3.5 text-xs text-white/80">
                        {p.average_rating > 0 && (
                          <span className="inline-flex items-center gap-1 font-bold text-amber-300">
                            <FiStar className="fill-amber-400 text-amber-400" size={13} />
                            <span>{p.average_rating.toFixed(1)}</span>
                            {p.reviews_count > 0 && (
                              <span className="text-white/50 font-normal">({p.reviews_count})</span>
                            )}
                          </span>
                        )}

                        <span className="inline-flex items-center gap-1 text-white/70">
                          <FiMapPin size={13} className="text-[var(--color-accent)]" />
                          <span>{p.location || "Niamey, Niger"}</span>
                        </span>

                        <span
                          className={`inline-flex items-center gap-1 font-semibold ${
                            stockInfo.urgency === "out"
                              ? "text-red-400"
                              : stockInfo.urgency === "low"
                              ? "text-amber-400"
                              : "text-emerald-400"
                          }`}
                        >
                          <FiBox size={13} />
                          <span>{stockInfo.cardText}</span>
                        </span>

                        <span className="inline-flex items-center gap-1 text-white/70">
                          <span>🇳🇪</span> Fast Niger Delivery
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-white/75 line-clamp-2 max-w-xl leading-relaxed">
                        {p.description ||
                          t(
                            "homePage.featureFallback",
                            "Curated premium selection sourced directly from verified suppliers across Niger."
                          )}
                      </p>

                      {/* Price & Action Row */}
                      <div className="flex items-center gap-6 pt-2">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-200/70">
                            Current Offer
                          </span>
                          <div className="flex items-baseline gap-2.5">
                            <span className="text-3xl xl:text-4xl font-black tracking-tight bg-gradient-to-r from-[#fff4cc] via-[#ffd700] to-[#ffaa00] bg-clip-text text-transparent drop-shadow-[0_2px_14px_rgba(255,215,0,0.35)]">
                              {formattedPrice}
                            </span>
                            {originalPrice && (
                              <span className="text-base font-semibold text-white/40 line-through">
                                {originalPrice}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Link
                            to={`/products/${p.id}`}
                            className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-gradient-to-r from-[#ff9f0a] via-[#ff7a00] to-[#ff5100] text-white text-sm font-extrabold shadow-[0_6px_22px_rgba(255,106,0,0.45)] ring-1 ring-amber-300/50 hover:ring-amber-200 hover:shadow-[0_8px_28px_rgba(255,120,0,0.6)] hover:scale-105 active:scale-95 transition-all no-underline"
                          >
                            <span>{t("homePage.orderNow", "Shop Now")}</span>
                            <FiArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                          </Link>

                          <Link
                            to={`/products/${p.id}`}
                            className="inline-flex items-center justify-center h-12 px-6 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-sm font-bold transition-all no-underline hover:border-white/35"
                          >
                            <span>View Details</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* ── Right Column: Interactive Featured Rail (Desktop Thumbnails) ── */}
              {featuredProducts.length > 1 && (
                <div className="absolute right-8 top-10 bottom-10 z-30 w-[240px] flex flex-col justify-between pointer-events-auto">
                  <div className="space-y-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-amber-300/80 px-1">
                      Featured Lineup
                    </span>
                    <div className="space-y-2">
                      {featuredProducts.slice(0, 3).map((item, railIdx) => {
                        const isRailActive = railIdx === currentSlide;
                        const railPrice = parseFloat(item.price) || 0;
                        const railFormattedPrice = formatPrice(
                          { min: railPrice, max: railPrice },
                          currency,
                          rates
                        );
                        const railImg =
                          item.featured_image_url ||
                          item.thumbnail_url ||
                          (item.images && item.images[0]?.image) ||
                          "/placeholder-image.jpg";

                        return (
                          <div
                            key={`rail-${item.id}`}
                            onClick={() => setCurrentSlide(railIdx)}
                            className={`p-2 rounded-2xl flex items-center gap-2.5 cursor-pointer transition-all duration-300 backdrop-blur-md ${
                              isRailActive
                                ? "bg-black/80 border-2 border-amber-400 shadow-[0_4px_20px_rgba(255,215,0,0.25)] scale-[1.03]"
                                : "bg-black/40 hover:bg-black/65 border border-white/10 opacity-70 hover:opacity-100 hover:scale-[1.01]"
                            }`}
                          >
                            <img
                              src={railImg}
                              alt={item.name}
                              className="w-12 h-12 rounded-xl object-cover shrink-0 bg-black/50"
                            />
                            <div className="overflow-hidden pr-1">
                              <h4 className="text-xs font-bold text-white truncate">
                                {item.name}
                              </h4>
                              <p className="text-[11px] font-black text-amber-300 truncate">
                                {railFormattedPrice}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Carousel Controls Bottom Right */}
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-1.5">
                      {featuredProducts.map((_, dotIdx) => (
                        <button
                          key={dotIdx}
                          onClick={() => setCurrentSlide(dotIdx)}
                          aria-label={`Slide ${dotIdx + 1}`}
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            dotIdx === currentSlide
                              ? "w-6 bg-gradient-to-r from-[#ffe259] to-[#ff9f0a] shadow-[0_0_10px_rgba(255,215,0,0.7)]"
                              : "w-1.5 bg-white/35 hover:bg-white/65"
                          }`}
                        />
                      ))}
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={handlePrev}
                        aria-label="Previous Slide"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-amber-500/30 hover:border-amber-400 text-white hover:text-amber-300 transition-all hover:scale-105 active:scale-95 shadow-md"
                      >
                        <FiChevronLeft size={15} />
                      </button>
                      <button
                        onClick={handleNext}
                        aria-label="Next Slide"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-amber-500/30 hover:border-amber-400 text-white hover:text-amber-300 transition-all hover:scale-105 active:scale-95 shadow-md"
                      >
                        <FiChevronRight size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ══════════════════════════════════════════════════════════════
                B. MOBILE SHOWCASE (Tailored for Mobile Phones < 1024px)
               ══════════════════════════════════════════════════════════════ */}
            {activeProduct && (() => {
              const mPriceValue = parseFloat(activeProduct.price) || 0;
              const mFormattedPrice = formatPrice(
                { min: mPriceValue, max: mPriceValue },
                currency,
                rates
              );
              const mOriginalPrice = activeProduct.discount_percentage > 0
                ? formatPrice(
                    { min: mPriceValue / (1 - activeProduct.discount_percentage / 100), max: mPriceValue / (1 - activeProduct.discount_percentage / 100) },
                    currency,
                    rates
                  )
                : null;
              const mStockInfo = formatStockDisplay(activeProduct.stock !== undefined ? activeProduct.stock : activeProduct.quantity);
              const mImageUrl =
                activeProduct.featured_image_url ||
                activeProduct.thumbnail_url ||
                (activeProduct.images && activeProduct.images[0]?.image) ||
                "/placeholder-image.jpg";

              return (
                <div
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                  className="block lg:hidden relative w-full rounded-[24px] overflow-hidden bg-[#0c0c0f] border border-amber-500/25 shadow-[0_12px_40px_rgba(0,0,0,0.25)]"
                >
                  {/* Golden Top Shimmer Edge */}
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#ffd700] to-transparent z-30 opacity-80 pointer-events-none" />

                  {/* 1. Mobile Landscape Image Container */}
                  <div className="relative w-full h-[190px] sm:h-[230px] overflow-hidden">
                    <img
                      src={mImageUrl}
                      alt={activeProduct.name}
                      className="w-full h-full object-cover object-center"
                    />

                    {/* Gradient Overlay for Image Depth */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-transparent to-black/35 pointer-events-none" />

                    {/* Top Floating Floating Badges */}
                    <div className="absolute top-3 inset-x-3 flex items-center justify-between z-20">
                      <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#ffe259] via-[#ffa751] to-[#ff6a00] px-2.5 py-0.5 text-[9.5px] font-black text-black tracking-wider uppercase shadow-[0_2px_10px_rgba(255,180,0,0.4)]">
                        <IoSparkles size={11} className="text-black" />
                        <span>Featured</span>
                      </span>

                      <div className="flex items-center gap-1.5">
                        {activeProduct.location && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-md text-[9px] font-medium text-white/90 border border-white/10">
                            <FiMapPin size={10} className="text-[var(--color-accent)]" />
                            <span>{activeProduct.location}</span>
                          </span>
                        )}

                        {activeProduct.average_rating > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-md text-[9px] font-bold text-amber-300 border border-amber-400/30">
                            <FiStar className="fill-amber-400 text-amber-400" size={10} />
                            <span>{activeProduct.average_rating.toFixed(1)}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Discount Tag on bottom-left of image */}
                    {activeProduct.discount_percentage > 0 && (
                      <span className="absolute bottom-2.5 left-3 z-20 inline-flex items-center rounded-full bg-red-500/90 backdrop-blur-md px-2 py-0.5 text-[9px] font-extrabold text-white shadow-sm">
                        -{activeProduct.discount_percentage}% OFF
                      </span>
                    )}
                  </div>

                  {/* 2. Mobile Bottom Information Sheet */}
                  <div className="p-4 bg-gradient-to-b from-black/95 to-[#111116] space-y-2.5 border-t border-amber-500/20">
                    {/* Category & Stock row */}
                    <div className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-2">
                        {activeProduct.category && (
                          <span className="font-semibold uppercase tracking-wider text-amber-200/80 px-2 py-0.5 bg-amber-500/10 rounded-full border border-amber-500/20">
                            {localizedCategoryName(activeProduct.category, i18n.language)}
                          </span>
                        )}

                        <span
                          className={`font-semibold ${
                            mStockInfo.urgency === "out"
                              ? "text-red-400"
                              : mStockInfo.urgency === "low"
                              ? "text-amber-400"
                              : "text-emerald-400"
                          }`}
                        >
                          {mStockInfo.cardText}
                        </span>
                      </div>

                      <span className="text-white/50 text-[10px]">🇳🇪 Niger Delivery</span>
                    </div>

                    {/* Title */}
                    <Link to={`/products/${activeProduct.id}`} className="no-underline block">
                      <h2 className="text-base sm:text-lg font-bold text-white tracking-tight leading-snug line-clamp-2">
                        {activeProduct.name}
                      </h2>
                    </Link>

                    {/* Price & Action Row */}
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex flex-col">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-amber-200/70">
                          Price
                        </span>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-xl sm:text-2xl font-black tracking-tight bg-gradient-to-r from-[#fff4cc] via-[#ffd700] to-[#ffaa00] bg-clip-text text-transparent">
                            {mFormattedPrice}
                          </span>
                          {mOriginalPrice && (
                            <span className="text-xs font-semibold text-white/40 line-through">
                              {mOriginalPrice}
                            </span>
                          )}
                        </div>
                      </div>

                      <Link
                        to={`/products/${activeProduct.id}`}
                        className="inline-flex items-center justify-center gap-1.5 h-9 px-5 rounded-full bg-gradient-to-r from-[#ff9f0a] via-[#ff7a00] to-[#ff5100] text-white text-xs font-extrabold shadow-[0_4px_18px_rgba(255,106,0,0.45)] ring-1 ring-amber-300/40 active:scale-95 transition-all no-underline"
                      >
                        <span>{t("homePage.orderNow", "Shop Now")}</span>
                        <FiArrowRight size={13} />
                      </Link>
                    </div>

                    {/* Mobile Navigation Dots & Chevrons */}
                    {featuredProducts.length > 1 && (
                      <div className="flex items-center justify-between pt-2 border-t border-white/5">
                        <div className="flex items-center gap-1.5">
                          {featuredProducts.map((_, dotIdx) => (
                            <button
                              key={dotIdx}
                              onClick={() => setCurrentSlide(dotIdx)}
                              aria-label={`Slide ${dotIdx + 1}`}
                              className={`h-1.5 rounded-full transition-all duration-300 ${
                                dotIdx === currentSlide
                                ? "w-5 bg-gradient-to-r from-[#ffe259] to-[#ff9f0a] shadow-[0_0_8px_rgba(255,215,0,0.6)]"
                                : "w-1.5 bg-white/30 hover:bg-white/60"
                              }`}
                            />
                          ))}
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={handlePrev}
                            aria-label="Previous Slide"
                            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white text-xs"
                          >
                            <FiChevronLeft size={13} />
                          </button>
                          <button
                            onClick={handleNext}
                            aria-label="Next Slide"
                            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white text-xs"
                          >
                            <FiChevronRight size={13} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          </div>
        </section>
      )}

      {/* ── 2. PRODUCT ROWS ── */}
      <main className="pb-12">
        <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8 space-y-6 sm:space-y-7">
          {/* New Arrivals Section */}
          {rankedNewArrivals.length > 0 && (
            <section className="pb-4 sm:pb-5 border-b border-black/[0.04] dark:border-white/[0.06]">
              <div className="flex items-baseline justify-between mb-2.5">
                <div className="flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-[var(--color-accent)] animate-pulse" />
                  <h2 className="text-xl sm:text-2xl lg:text-[1.75rem] font-semibold tracking-[-0.025em] text-[var(--color-text-primary)]">
                    {t("navbar.newArrivals", "New Arrivals")}
                  </h2>
                </div>
                <Link
                  to="/new-arrivals"
                  className="group text-[13px] font-medium text-[var(--color-accent)] hover:underline inline-flex items-center gap-1 no-underline"
                >
                  <span>{t("profilePage.viewAll", "See all")}</span>
                  <FiChevronRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>

              <div className="flex gap-3 sm:gap-4 overflow-x-auto py-2 -my-1 no-scrollbar snap-x snap-mandatory -mx-2 px-2">
                {rankedNewArrivals.slice(0, 10).map((product) => (
                  <div
                    key={product.id}
                    className="w-[160px] sm:w-[175px] md:w-[195px] shrink-0 snap-start flex flex-col"
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Categories Rows */}
          <div className="space-y-6 sm:space-y-7">
            {categories.map((cat) => {
              const products = categoryProducts[cat.id] || [];
              const categoryName = localizedCategoryName(cat, i18n.language);
              if (products.length === 0) return null;

              return (
                <section
                  key={cat.id}
                  className="pb-4 sm:pb-5 border-b border-black/[0.04] dark:border-white/[0.06] last:border-b-0"
                >
                  <div className="flex items-baseline justify-between mb-2.5">
                    <div className="flex items-baseline gap-2.5">
                      <h2 className="text-xl sm:text-2xl lg:text-[1.75rem] font-semibold tracking-[-0.025em] text-[var(--color-text-primary)]">
                        {categoryName}
                      </h2>
                      <span className="hidden sm:inline-block text-[11px] font-medium text-[var(--color-text-tertiary)]">
                        • {products.length}+ items
                      </span>
                    </div>
                    <Link
                      to={`/category/${cat.id}/${encodeURIComponent(categoryName)}`}
                      className="group text-[13px] font-medium text-[var(--color-accent)] hover:underline inline-flex items-center gap-1 no-underline"
                    >
                      <span>{t("searchResults.allProducts", "See all")}</span>
                      <FiChevronRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </div>

                  <div className="flex gap-3 sm:gap-4 overflow-x-auto py-2 -my-1 no-scrollbar snap-x snap-mandatory -mx-2 px-2">
                    {products.slice(0, 10).map((product) => (
                      <div
                        key={product.id}
                        className="w-[160px] sm:w-[175px] md:w-[195px] shrink-0 snap-start flex flex-col"
                      >
                        <ProductCard product={product} />
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          {/* Best Sellers Section */}
          {rankedBestSellers.length > 0 && (
            <section className="pb-4 sm:pb-5">
              <div className="flex items-baseline justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <FiTrendingUp className="text-[var(--color-accent)]" size={17} />
                  <h2 className="text-xl sm:text-2xl lg:text-[1.75rem] font-semibold tracking-[-0.025em] text-[var(--color-text-primary)]">
                    {t("footer.bestSellers", "Best Sellers")}
                  </h2>
                </div>
                <Link
                  to="/flash-deals"
                  className="group text-[13px] font-medium text-[var(--color-accent)] hover:underline inline-flex items-center gap-1 no-underline"
                >
                  <span>{t("profilePage.viewAll", "See all")}</span>
                  <FiChevronRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>

              <div className="flex gap-3 sm:gap-4 overflow-x-auto py-2 -my-1 no-scrollbar snap-x snap-mandatory -mx-2 px-2">
                {rankedBestSellers.slice(0, 10).map((product) => (
                  <div
                    key={product.id}
                    className="w-[160px] sm:w-[175px] md:w-[195px] shrink-0 snap-start flex flex-col"
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default HomePage;
